import {
  createSession,
  getCurrentQuestion,
  submitAnswer,
  getResult,
  goBack,
} from "./engine";
import type { Session, Answer, AnswerRecord, Audience } from "./engine";
import { ruleSets as rulesets } from "./rules/registry";
import { renderWelcome } from "./ui/welcome";
import { renderGate, FLORIDA_GATE } from "./ui/gate";
import { renderOfframp } from "./ui/offramp";
import { renderQuestion } from "./ui/questionnaire";
import { renderDiagnosisSelect } from "./ui/diagnosis-select";
import { renderContact } from "./ui/contact";
import type { ContactData } from "./ui/contact";
import { renderReferral, professionalPdfPairs } from "./ui/referral";
import type { ProfessionalDetails } from "./ui/referral";
import { renderConfirmation } from "./ui/results";
import { buildModelFromSession } from "./pdf/from-session";
import { renderPdf } from "./pdf";
import { getTurnstileToken } from "./ui/turnstile";
import { submitAssessment, newIdempotencyKey } from "./submit/client";
import { t } from "./ui/i18n";
import { mountInstallButton } from "./ui/install";
import "./style.css";

const app = document.getElementById("app");
if (!app) {
  throw new Error("Could not find #app element");
}

const installSlot = document.getElementById("install-slot");
if (installSlot) mountInstallButton(installSlot);

const SUBMIT_ENDPOINT = import.meta.env.VITE_SUBMIT_ENDPOINT ?? "";
const TURNSTILE_SITEKEY = import.meta.env.VITE_TURNSTILE_SITEKEY ?? "";

let assessmentInProgress = false;

window.addEventListener("beforeunload", (e: BeforeUnloadEvent) => {
  if (!assessmentInProgress) return;
  e.preventDefault();
  e.returnValue = "";
});

let session: Session;
let previousAnswerForDisplay: Answer | undefined;
let selectedAudience: Audience = "family";
// One idempotency key per completed assessment, stable across submit retries.
let idempotencyKey = "";
let professionalDetails: ProfessionalDetails | null = null;

function showWelcome(): void {
  assessmentInProgress = false;
  renderWelcome(app!, (audience) => {
    selectedAudience = audience;
    showGate();
  });
}

function showGate(): void {
  renderGate(app!, FLORIDA_GATE, {
    onPass: showDiagnosisSelect,
    onFail: showOfframp,
  });
}

function showOfframp(): void {
  assessmentInProgress = false;
  renderOfframp(app!, showWelcome);
}

function showDiagnosisSelect(): void {
  renderDiagnosisSelect(
    app!,
    (diagnosisKey) => {
      const rules = rulesets[diagnosisKey];
      session = createSession(rules, { locale: "en", audience: selectedAudience });
      assessmentInProgress = true;
      previousAnswerForDisplay = undefined;
      // A fresh assessment gets a fresh idempotency key. Without this reset a key left over
      // from a prior failed submit would be reused, and the relay would dedupe the new
      // patient's submission as a duplicate and silently drop it.
      idempotencyKey = "";
      professionalDetails = null;
      showQuestion();
    },
    showGate
  );
}

function showQuestion(): void {
  const question = getCurrentQuestion(session);
  if (!question) {
    if (getResult(session)) showReview();
    return;
  }
  const questionNumber = session.history.length + 1;
  const onBack = session.history.length > 0 ? handleBack : showDiagnosisSelect;
  renderQuestion(app!, question, questionNumber, handleAnswer, onBack, previousAnswerForDisplay);
  previousAnswerForDisplay = undefined;
}

function handleAnswer(answer: Answer): void {
  session = submitAnswer(session, answer);
  if (getResult(session)) showReview();
  else showQuestion();
}

function handleBack(): void {
  const lastRecord: AnswerRecord | undefined = session.history[session.history.length - 1];
  previousAnswerForDisplay = lastRecord?.answer;
  session = goBack(session);
  showQuestion();
}

// Review step: confirm before entering contact details. The determination is not shown.
function showReview(): void {
  while (app!.firstChild) app!.removeChild(app!.firstChild);

  const wrapper = document.createElement("div");
  wrapper.className = "questionnaire";

  const heading = document.createElement("h2");
  heading.className = "question-text";
  heading.textContent = t("ui.confirm.ready_title");
  wrapper.appendChild(heading);

  const description = document.createElement("p");
  description.className = "help-text";
  description.textContent = t("ui.confirm.ready_description");
  wrapper.appendChild(description);

  const actions = document.createElement("div");
  actions.className = "confirmation-actions";

  const continueBtn = document.createElement("button");
  continueBtn.type = "button";
  continueBtn.className = "btn-primary";
  continueBtn.textContent = t("ui.confirm.generate");
  continueBtn.addEventListener("click", () => {
    if (selectedAudience === "professional") showReferral();
    else showContact();
  });
  actions.appendChild(continueBtn);

  const backBtn = document.createElement("button");
  backBtn.type = "button";
  backBtn.className = "btn-secondary";
  backBtn.textContent = t("ui.nav.back");
  backBtn.addEventListener("click", handleBack);
  actions.appendChild(backBtn);

  wrapper.appendChild(actions);
  app!.appendChild(wrapper);
}

function showReferral(): void {
  renderReferral(
    app!,
    {
      onContinue: (details) => {
        professionalDetails = details;
        showContact();
      },
      onBack: showReview,
    },
    professionalDetails ?? undefined
  );
}

function showContact(): void {
  const api = renderContact(app!, {
    onSubmit: async (data: ContactData) => {
      // Guard before entering the busy state so a missing result never strands the button.
      const result = getResult(session);
      if (!result) return;

      clearSubmitError();
      api.setSubmitting(true);
      if (!idempotencyKey) idempotencyKey = newIdempotencyKey();

      try {
        const pdfPairs = professionalDetails ? professionalPdfPairs(professionalDetails) : undefined;
        const model = buildModelFromSession(session, result, formatToday(), pdfPairs);
        const pdfBlob = renderPdf(model);
        const turnstileToken = await getTurnstileToken(TURNSTILE_SITEKEY);

        const outcome = await submitAssessment({
          endpoint: SUBMIT_ENDPOINT,
          pdfBlob,
          condition: model.conditionName,
          determination: result.determination,
          requester: {
            name: data.name,
            email: data.email,
            phone: data.phone,
            relationship: data.relationship,
          },
          consent: data.consent,
          professional: professionalDetails ?? undefined,
          turnstileToken,
          idempotencyKey,
        });

        if (outcome.status === "success") {
          assessmentInProgress = false;
          idempotencyKey = "";
          professionalDetails = null;
          renderConfirmation(app!, showWelcome);
        } else {
          api.setSubmitting(false);
          showSubmitError();
        }
      } catch {
        // PDF build, Turnstile token fetch, or the POST threw. Surface the same polite error
        // and re-enable submit so the user can retry (the idempotency key stays stable).
        api.setSubmitting(false);
        showSubmitError();
      }
    },
    onBack: selectedAudience === "professional" ? showReferral : showReview,
  });
}

// Inline, polite error surfaced above the contact form's submit. Retry re-runs the submit,
// reusing the stable idempotency key so a retry never double-sends.
function showSubmitError(): void {
  const existing = app!.querySelector(".submit-error");
  if (existing) return;
  const banner = document.createElement("p");
  banner.className = "submit-error field-error";
  banner.setAttribute("role", "alert");
  banner.textContent = t("ui.submit.error");
  const form = app!.querySelector("form");
  form?.prepend(banner);
}

function clearSubmitError(): void {
  app!.querySelector(".submit-error")?.remove();
}

function formatToday(): string {
  const now = new Date();
  return now.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

showWelcome();
